import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import type {
  UserDefinedFieldFormValues,
  VariableType,
  VariableContext,
  SelectOption,
} from "../types/UserDefinedField";
import TextField from "@components/FormsV2/TextField/TextField";
import FormNumberFieldV2 from "@components/FormsV2/FormNumberFieldV2/FormNumberFieldV2";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import { DropdownListItems } from "./DropdownListItems";
import "./style.css";

export interface UserDefinedFieldFormProps {
  id: string;
  initialValues: UserDefinedFieldFormValues;
  onSubmit: (values: UserDefinedFieldFormValues) => void;
  isSubmitting?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

const VARIABLE_TYPES: { value: VariableType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
];

const VARIABLE_CONTEXTS: { value: VariableContext; label: string }[] = [
  { value: "client", label: "Client" },
  { value: "crm_opportunity", label: "CRM Opportunity" },
  { value: "project", label: "Project" },
  { value: "governance", label: "Governance" },
  { value: "service_location", label: "Service Location" },
  { value: "project_contact", label: "Project Contact" },
  { value: "product", label: "Product" },
  { value: "resource", label: "Resource" },
  { value: "section", label: "Section" },
  { value: "service", label: "Service" },
  { value: "teammate", label: "Teammate" },
];

export const UserDefinedFieldForm = ({
  id,
  initialValues,
  onSubmit,
  isSubmitting: _isSubmitting,
  onDirtyChange,
}: UserDefinedFieldFormProps) => {
  const [values, setValues] =
    useState<UserDefinedFieldFormValues>(initialValues);
  const update = <K extends keyof UserDefinedFieldFormValues>(
    key: K,
    val: UserDefinedFieldFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: val }));

  const initialJson = useMemo(
    () => JSON.stringify(initialValues),
    [initialValues]
  );
  const isDirty = JSON.stringify(values) !== initialJson;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  return (
    <Form
      id={id}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(values);
      }}
    >
      <div className="adminFormSectionTitle">Identifier & Assignment</div>
      <div className="row">
        <div className="col-sm-4">
          <Form.Label htmlFor="udf-label">* Description</Form.Label>
          <TextField
            label={null}
            id="udf-label"
            value={values.label}
            placeholder="Enter a description"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              update("label", e.target.value)
            }
            required
          />
        </div>
        <div className="col-sm-4">
          <Form.Label htmlFor="udf-name">* Field name</Form.Label>
          <TextField
            label={null}
            id="udf-name"
            value={values.name}
            placeholder="Enter a field name"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              update("name", e.target.value)
            }
            required
          />
        </div>
        <div className="col-sm-4">
          <RequiredReactSelect
            label="* Where Will This Be Used"
            value={
              VARIABLE_CONTEXTS.find((o) => o.value === values.context) ??
              VARIABLE_CONTEXTS[0]
            }
            onChange={(opt: any) =>
              update(
                "context",
                (opt?.value ?? VARIABLE_CONTEXTS[0].value) as VariableContext
              )
            }
            options={VARIABLE_CONTEXTS}
            required
          />
        </div>
      </div>

      <div className="adminFormSectionTitle">Type & Options</div>
      <div className="row">
        <div className="col-sm-4">
          <RequiredReactSelect
            label="* Type"
            value={
              VARIABLE_TYPES.find((o) => o.value === values.variableType) ??
              VARIABLE_TYPES[0]
            }
            onChange={(opt: any) =>
              update(
                "variableType",
                (opt?.value ?? VARIABLE_TYPES[0].value) as VariableType
              )
            }
            options={VARIABLE_TYPES}
            required
          />
        </div>
        <div className="col-sm-4">
          <FormNumberFieldV2
            label="Minimum Length"
            id="udf-minimum"
            value={values.minimum ?? ""}
            placeholder="Enter a minimum character length"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              update(
                "minimum",
                e.target.value === "" ? null : Number(e.target.value)
              )
            }
          />
        </div>
        <div className="col-sm-4">
          <FormNumberFieldV2
            label="Maximum Length"
            id="udf-maximum"
            value={values.maximum ?? ""}
            placeholder="Enter a maximum character length"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              update(
                "maximum",
                e.target.value === "" ? null : Number(e.target.value)
              )
            }
          />
        </div>
      </div>
      <div className="row">
        <div className="col-sm-4 udfToggleCell">
          <Form.Label>Is this field required?</Form.Label>
          <GenericToggle
            key={`req-${initialValues.required}`}
            initialValueOfIsOn={values.required}
            onToggle={(v: boolean) => update("required", v)}
            onText="Required"
            offText="Optional"
            hasText
          />
        </div>
      </div>

      <div className="adminFormSectionTitle">Dropdown List Items</div>
      <DropdownListItems
        options={values.selectOptions}
        onChange={(opts: SelectOption[]) => update("selectOptions", opts)}
      />
    </Form>
  );
};
